/**
 * EditionSelector — 카드 에디션(언어판) 선택 블록 (PKA 화면정의서 SC-009).
 *
 * IA v0.1(rev) 진입 플로우: **에디션을 먼저 고르고, 그 선택을 기준으로 카드를 표시한다.**
 * 선택은 화면 하나에 갇히지 않고 검색·세트·카드 상세까지 이어진다 (`EditionContext`).
 *
 * 홈·검색 두 진입 화면에 같은 블록으로 노출한다. 컴포넌트로 묶어둔 이유는
 * 두 화면의 문구·순서·기본값이 갈라지면 "어느 시장 기준인가"가 화면마다 달라 보이기
 * 때문이다 — 이 블록이 곧 그 답이라 한 벌만 존재해야 한다.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ToggleButton } from "@astryxdesign/core/ToggleButton";
import { HStack, StackItem } from "@astryxdesign/core/Layout";
import { EDITIONS } from "../../data/priceModel";
import { useEdition } from "./EditionContext";

interface EditionSelectorProps {
  /**
   * 칩 행 **가장 우측**에 붙는 부가 요소 (홈의 시세 갱신 시각·새로고침).
   * 화면마다 다른 것이라 이 컴포넌트가 알 필요는 없고, 자리만 내준다.
   */
  endContent?: ReactNode;
}

export default function EditionSelector({ endContent }: EditionSelectorProps) {
  const { t } = useTranslation("card");
  const { edition, setEdition } = useEdition();

  return (
    // '카드 에디션' 제목은 화면에서 뺐다 — 칩 라벨(한국판/영문판/일본판)이 이미
    // 무엇을 고르는지 말한다. 다만 눈으로 못 읽는 사용자에게는 이 묶음이 뭔지
    // 알려줄 이름이 필요하므로 role=group + aria-label 로만 남긴다.
    <HStack gap={2} align="center" wrap="wrap" role="group" aria-label={t("edition.selectLabel")}>
      {/* 칩 UI — 세트 상세의 필터 칩과 같은 어휘를 쓴다. 다만 필터와 달리 에디션은
          **항상 하나가 선택돼 있어야** 하므로, 눌린 칩을 다시 눌러도 해제되지 않는다
          (onPressedChange 의 다음 상태를 무시하고 그 에디션으로 고정). */}
      <HStack gap={1} wrap="wrap">
        {EDITIONS.map((e) => (
          <ToggleButton
            key={e}
            size="sm"
            data-compact=""
            label={t(`edition.${e}`)}
            isPressed={e === edition}
            onPressedChange={() => setEdition(e)}
          />
        ))}
      </HStack>
      {/* size="fill" 로 남는 폭을 먹고 안에서 오른쪽 정렬 — 칩과 한 줄에 들어가면
          줄 끝에, 좁아서 다음 줄로 밀리면 그 줄의 오른쪽 끝에 붙는다. */}
      {endContent && (
        <StackItem size="fill">
          <HStack gap={1} align="center" justify="end">{endContent}</HStack>
        </StackItem>
      )}
    </HStack>
  );
}
